import { useEffect, useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { PROVINCES } from "../../lib/constants";
import { DistanceFilter } from "../DistanceFilter";
import { BannerAd } from "../BannerAd";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const Field = ({ label, children }) => (
  <div><label className="text-sm font-medium text-[#12234A]">{label}</label>{children}</div>
);

const PickSelect = ({ value, onChange, testId, children }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className="mt-1 h-10 rounded-xl"><SelectValue /></SelectTrigger>
    <SelectContent className="bg-white">{children}</SelectContent>
  </Select>
);

export function FilterSidebar({ t, params, setParams, setParam, isLF, onClear }) {
  const [f, setF] = useState({});
  const key = params.toString();
  useEffect(() => {
    setF({ q: params.get("q") || "", city: params.get("city") || "", min_price: params.get("min_price") || "", max_price: params.get("max_price") || "" });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const enter = (e) => e.key === "Enter" && apply();

  const apply = () => {
    const next = new URLSearchParams(params);
    Object.entries(f).forEach(([k, v]) => (v ? next.set(k, v.trim ? v.trim() : v) : next.delete(k)));
    if (f.q !== (params.get("q") || "")) next.delete("sort");
    next.delete("page");
    setParams(next);
  };

  return (
    <aside className="lg:col-span-3">
      <div className="lg:sticky lg:top-32 lg:max-h-[calc(100vh-9rem)] lg:overflow-y-auto no-scrollbar">
        <div className="bg-white rounded-2xl border border-slate-200/80 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2"><SlidersHorizontal size={18} /> {t("br_filters")}</h3>
            <button onClick={onClear} className="text-xs text-slate-400 hover:text-[#F5551E]" data-testid="clear-filters">{t("br_clear")}</button>
          </div>
          <div className="space-y-4">
            <Field label={t("br_keyword")}>
              <div className="relative mt-1">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={f.q || ""} onChange={set("q")} onKeyDown={enter} data-testid="filter-keyword" className="pl-9 h-10 rounded-xl" placeholder="e.g. retriever" />
              </div>
            </Field>
            <Field label={t("br_province")}>
              <PickSelect value={params.get("province") || "all"} onChange={(v) => setParam("province", v)} testId="filter-province">
                {PROVINCES.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
              </PickSelect>
            </Field>
            <Field label={t("br_verified")}>
              <PickSelect value={params.get("verified") || "all"} onChange={(v) => setParam("verified", v)} testId="filter-verified">
                <SelectItem value="all">{t("br_verified_all")}</SelectItem>
                <SelectItem value="business">{t("lc_verified_business")}</SelectItem>
                <SelectItem value="breeder">{t("lc_verified_breeder")}</SelectItem>
                <SelectItem value="seller">{t("lc_verified_seller")}</SelectItem>
              </PickSelect>
            </Field>
            <Field label={t("br_city")}>
              <Input value={f.city || ""} onChange={set("city")} onKeyDown={enter} data-testid="filter-city" className="mt-1 h-10 rounded-xl" placeholder="e.g. Toronto" />
            </Field>
            <DistanceFilter params={params} setParams={setParams} />
            {!isLF && <Field label={t("br_price_range")}>
              <div className="flex gap-2 mt-1">
                <Input value={f.min_price || ""} onChange={set("min_price")} onKeyDown={enter} data-testid="filter-min-price" type="number" placeholder={t("br_min")} className="h-10 rounded-xl" />
                <Input value={f.max_price || ""} onChange={set("max_price")} onKeyDown={enter} data-testid="filter-max-price" type="number" placeholder={t("br_max")} className="h-10 rounded-xl" />
              </div>
            </Field>}
            <Button onClick={apply} data-testid="apply-filters" className="w-full h-10 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold">{t("br_apply")}</Button>
          </div>
        </div>
        <BannerAd variant="sidebar" className="mt-5 hidden lg:block" />
      </div>
    </aside>
  );
}
